// src/pages/Login/login.tsx

import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { login, selectAuth } from "../../store/auth_slice";
import Modal from "../../common/modal/modal";
import { AppDispatch } from "../../store/store";
import Button from "../../common/button/button";
import FormInput from "../../common/form_input/form_input";
import ToastService from "../../common/toast/toast_service";

/**
 * Interface for Login Page props
 */
interface LoginPageProps {
  isOpen: boolean;
  onClose: () => void;
  openSignUpModal: () => void;
}

/**
 * LoginPage Component
 * Handles user authentication through a modal form
 */
const LoginPage: React.FC<LoginPageProps> = ({
  isOpen,
  onClose,
  openSignUpModal,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { loading, error, isAuthenticated, user } = useSelector(selectAuth);

  // Validation schema for the login form
  const validationSchema = Yup.object({
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    password: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("Password is required"),
  });

  // Initialize form handling with Formik
  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      const result = await dispatch(login(values));

      if ('error' in result) {
        ToastService.showError(result.error || "Login failed. Please try again.");
      } else {
        ToastService.showSuccess("Login successful!");
        onClose();
      }
    },
  });

  // Effect to handle successful authentication
  React.useEffect(() => {
    if (isAuthenticated && user) {
      onClose();
    }
  }, [isAuthenticated, user, onClose]);

  // Handler for switching to signup modal
  const handleSignUpClick = () => {
    onClose();
    openSignUpModal();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="w-full max-w-md mx-auto">
        {/* Modal Header */}
        <h2 className="text-base font-ubuntu font-medium mb-2">
          Login
        </h2>
        <div className="border-b-2" />
        <div className="text-center my-6 font-ubuntu font-medium text-primary">
          Welcome Back!
        </div>

        {/* Login Form */}
        <form onSubmit={formik.handleSubmit} className="px-6">
          {/* Email Input */}
          <FormInput
            label="Email"
            type="email"
            name="email"
            value={formik.values.email}
            placeholder="Enter your email"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            touched={formik.touched.email ?? false}
            error={formik.errors.email}
            className="mb-4"
          />

          {/* Password Input */}
          <FormInput
            label="Password"
            type="password"
            name="password"
            value={formik.values.password}
            placeholder="Enter your password"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            touched={formik.touched.password ?? false}
            error={formik.errors.password}
            inputStyle="text-grayPass"
            className="mb-4"
          />

          {/* Display Error Message if any */}
          {error && (
            <div className="text-red-500 text-sm mb-4 text-center">
              {error}
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            buttonType="primary"
            className="w-full bg-button font-ubuntu text-xl text-white p-3 rounded-lg hover:bg-indigo-800 transition duration-200 ease-in-out"
            disabled={loading}
          >
            {loading
              ? "Logging in..."
              : "Login"}
          </Button>

          {/* Forgot Password Link */}
          <div className="text-center mt-4 mb-6">
            <span
              className="text-primary text-sm cursor-pointer hover:underline"
              onClick={() => {/* TODO: Implement forgot password functionality */ }}
            >
              Forgot Password?
            </span>
          </div>
        </form>

        {/* Sign Up Link */}
        <div className="px-6 my-6 font-ubuntu text-primary text-xs text-center">
          Don't have an account?{" "}
          <span
            className="underline font-medium text-base cursor-pointer hover:text-indigo-800 transition-colors duration-200"
            onClick={handleSignUpClick}
          >
            Sign up
          </span>
        </div>
      </div>
    </Modal>
  );
};

export default LoginPage;
